import React from 'react';
import { PieChart, Utensils, Coffee, Clock, Check, X } from 'lucide-react';
import ToolResult from '../../../../../components/ui/ToolResult';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент отображения рекомендуемого распределения макронутриентов и плана питания
 */
const NutritionRatios = ({ nutritionPlan, results }) => {
  if (!nutritionPlan || !results) return null;

  const { calories, macros, mealPlan, restrictedFoods, recommendedFoods } = nutritionPlan;

  // Рассчитываем проценты макронутриентов для диаграммы
  const totalCalories = 
    (macros.protein * 4) + 
    (macros.carbs * 4) + 
    (macros.fat * 9);
  
  const proteinPercent = Math.round((macros.protein * 4 / totalCalories) * 100);
  const carbsPercent = Math.round((macros.carbs * 4 / totalCalories) * 100);
  const fatPercent = Math.round((macros.fat * 9 / totalCalories) * 100);

  return (
    <ToolResult title="Рекомендации по питанию">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Макронутриенты */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <PieChart className="w-5 h-5 mr-2 text-emerald-500" />
            Макронутриенты
          </h3>
          
          <div className="space-y-4">
            {/* Диаграмма макронутриентов (визуализация в виде полос) */}
            <div className="flex h-8 rounded-lg overflow-hidden">
              <div 
                className="bg-emerald-500 text-white text-xs flex items-center justify-center"
                style={{ width: `${proteinPercent}%` }}
                title={`Белки: ${proteinPercent}%`}
              >
                {proteinPercent}%
              </div>
              <div 
                className="bg-amber-500 text-white text-xs flex items-center justify-center"
                style={{ width: `${fatPercent}%` }}
                title={`Жиры: ${fatPercent}%`}
              >
                {fatPercent}%
              </div>
              <div 
                className="bg-blue-500 text-white text-xs flex items-center justify-center"
                style={{ width: `${carbsPercent}%` }}
                title={`Углеводы: ${carbsPercent}%`}
              >
                {carbsPercent}%
              </div>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center">
                <span className="w-3 h-3 inline-block bg-emerald-500 rounded-sm mr-2"></span>
                Белки: {macros.protein}г ({formatNumber(macros.protein * 4, 0)} ккал)
              </span>
              <span className="text-gray-600 dark:text-gray-400">{proteinPercent}%</span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center">
                <span className="w-3 h-3 inline-block bg-amber-500 rounded-sm mr-2"></span>
                Жиры: {macros.fat}г ({formatNumber(macros.fat * 9, 0)} ккал)
              </span>
              <span className="text-gray-600 dark:text-gray-400">{fatPercent}%</span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center">
                <span className="w-3 h-3 inline-block bg-blue-500 rounded-sm mr-2"></span>
                Углеводы: {macros.carbs}г ({formatNumber(macros.carbs * 4, 0)} ккал)
              </span>
              <span className="text-gray-600 dark:text-gray-400">{carbsPercent}%</span>
            </div>
            
            <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200 dark:border-gray-700 font-medium">
              <span>Общее количество калорий:</span>
              <span>{formatNumber(calories, 0)} ккал</span>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-800 dark:text-blue-300">
            <h4 className="font-medium mb-1 flex items-center">
              <Coffee className="w-4 h-4 mr-1" /> 
              Дневная норма воды:
            </h4>
            <p>
              {formatNumber(results.weight.value * (results.gender === 'male' ? 35 : 31), 0)} мл
              {results.activityLevel === 'high' || results.activityLevel === 'very_high' 
                ? ' (при интенсивных тренировках +500-1000 мл)' 
                : ''}
            </p>
          </div>
        </div>
        
        {/* Распределение по приемам пищи */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-emerald-500" />
            Приемы пищи
          </h3>
          
          <div className="space-y-3">
            {mealPlan.map((meal, index) => (
              <div key={index} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex justify-between items-center mb-1">
                  <span className="font-medium">{meal.name}</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    ~{formatNumber(calories * meal.percentage, 0)} ккал
                  </span>
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  <div className="flex gap-2">
                    <span>Б: {formatNumber(macros.protein * meal.percentage, 0)}г</span>
                    <span>Ж: {formatNumber(macros.fat * meal.percentage, 0)}г</span>
                    <span>У: {formatNumber(macros.carbs * meal.percentage, 0)}г</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Рекомендуемые и нерекомендуемые продукты */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-3 flex items-center">
            <Check className="w-5 h-5 mr-2 text-emerald-500" />
            Рекомендуемые продукты
          </h3>
          <ul className="list-disc list-inside space-y-1 text-sm pl-1">
            {recommendedFoods.map((food, index) => (
              <li key={index} className="text-gray-700 dark:text-gray-300">{food}</li>
            ))}
          </ul>
        </div>
        
        {restrictedFoods.length > 0 && (
          <div>
            <h3 className="text-base font-medium text-gray-900 dark:text-white mb-3 flex items-center">
              <X className="w-5 h-5 mr-2 text-red-500" />
              Ограничить/исключить
            </h3>
            <ul className="list-disc list-inside space-y-1 text-sm pl-1">
              {restrictedFoods.map((food, index) => (
                <li key={index} className="text-gray-700 dark:text-gray-300">{food}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
      
      {/* Советы по питанию */}
      <div className="mt-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
        <h3 className="text-base font-medium text-emerald-800 dark:text-emerald-300 mb-2 flex items-center">
          <Utensils className="w-5 h-5 mr-2" />
          Советы по питанию для {results.goal.includes('lose') 
            ? 'похудения' 
            : results.goal.includes('gain') 
              ? 'набора массы' 
              : 'поддержания веса'}
        </h3>
        <ul className="space-y-2 text-sm text-emerald-700 dark:text-emerald-400">
          {results.goal.includes('lose') ? (
            <>
              <li>Отдавайте предпочтение белковой пище, она дольше сохраняет чувство сытости</li>
              <li>Увеличьте потребление клетчатки (овощи, зелень) для снижения калорийности рациона</li>
              <li>Пейте воду за 15-20 минут до еды, это уменьшает аппетит</li>
              <li>Исключите или минимизируйте употребление сахара и простых углеводов</li>
            </>
          ) : results.goal.includes('gain') ? (
            <>
              <li>Увеличьте частоту приёмов пищи до 5-6 раз в день</li>
              <li>Включите высококалорийные, но полезные продукты (орехи, авокадо, оливковое масло)</li>
              <li>Потребляйте достаточно белка (1.6-2.2г на кг веса) для роста мышечной массы</li>
              <li>Пейте калорийные смузи или протеиновые коктейли для удобного увеличения калорий</li>
            </>
          ) : (
            <>
              <li>Придерживайтесь сбалансированного питания с разнообразными продуктами</li>
              <li>Старайтесь соблюдать режим питания, питаясь в одно и то же время</li>
              <li>Следите за размерами порций, чтобы не переедать</li>
              <li>Включайте все группы продуктов для получения необходимых микроэлементов</li>
            </>
          )}
          <li>Старайтесь получать питательные вещества из натуральных продуктов, а не добавок</li>
          <li>Планируйте рацион заранее, это поможет избежать импульсивных решений</li>
        </ul>
      </div>
    </ToolResult>
  );
};

export default NutritionRatios;